import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  TableContainer,
  CircularProgress,
  Typography,
  IconButton,
  ImageList,
  ImageListItem,
  Modal,
  Button,
  Snackbar,
  TextField,
  Alert,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";

import { MAX_UPLOAD_FILE_SIZE } from "../../constants";
import { formatTimestamp } from "../../../utils/date";
import { IUploadFileName, updateFileNameEmpoyee } from "../../../api/documents";
import {
  getfileUserEmployee,
  SetDocumentEmployeeUpload,
} from "../../../api/documents";
import useSnackbar from "../../../hooks/useSnackbar";
import { IFileData } from "../../../interface/IFileData";
import useFileDownloader from "../../../hooks/useFileDownloader";
import { deleteFileEmployee } from "../../../api/user-program";
import { DeletePostModal } from "../modal-delete";
import { useDocxViewer } from "../../../hooks/useDocxViewer";
import { UploadModalEmployee } from "./upload-modal-employee";
import { UserProgramContext } from "../../../context/user-program/user-program";

export const UploadFilePage = () => {
  const { role } = useContext(UserProgramContext);
  const [fileEmployee, setFileEmployee] = useState<IFileData[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [newUploadModalOpen, setNewUploadModalOpen] = useState(false);
  const { patient } = useParams() as { patient: string };
  const [loading, setLoading] = useState(false);
  const {
    handleDownloadClicked,
    handleDownloadTemporaryLink,
    setIsDownloading,
    setIsFileDetailSelected,
    isDownloading,
    isAPdfFile,
    isFileDetailSelected,
    temporaryUrl,
    isDocxFile,
  } = useFileDownloader();

  const docxContent = useDocxViewer(temporaryUrl);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newName, setNewName] = useState("");

  const { message, severity, open, showSnackbar, closeSnackbar } =
    useSnackbar();

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getfileUserEmployee(patient);
      setFileEmployee(response.files);
    } catch (error: any) {
      console.error("Error fetching sections:", error.response.data.message);
    } finally {
      setLoading(false);
      setFileName("");
    }
  }, [patient]);

  const filesNamesMap = new Map<string, string>([
    ["psychologisches-gutachten", "Psychologisches Gutachten"],
    ["mmk-befund", "MMK-Befund"],
  ]);

  //upload file
  const handleFileChange = async (event: any) => {
    const [file] = event.target?.files;
    setIsDownloading(true);

    console.log(file);

    if (file) {
      try {
        if (file.size > MAX_UPLOAD_FILE_SIZE) {
          return;
        }
        const selectedFileType = filesNamesMap.get(fileName);
        await SetDocumentEmployeeUpload({
          name: selectedFileType ? selectedFileType : file.name,
          file,
          userId: patient,
          onProgress: (percentCompleted) => {},
        });
        showSnackbar("Das Dokument wurde erfolgreich hochgeladen", "success");
      } catch (error: any) {
        console.error("Error uploading file:", error);
        showSnackbar("Beim Hochladen ist ein Fehler aufgetreten.", "error");
      } finally {
        setIsDownloading(false);
      }
    }
  };

  //logic to name change
  const handleSave = async (index: number, _id: string, newName: string) => {
    const updateFile: IUploadFileName = {
      fileId: _id,
      newName,
    };
    try {
      await updateFileNameEmpoyee(updateFile);
      showSnackbar(
        "Die Namensänderung wurde erfolgreich durchgeführt. ",
        "success"
      );
    } catch (error: any) {
      console.error("Error updating file name:", error.message);
      showSnackbar(
        "Beim Ändern des Namens ist ein Fehler aufgetreten.",
        "error"
      );
    }

    setEditingIndex(null);
    setNewName("");
    fetchSections();
  };

  const handleBlur = (index: number, id: string, newName: string) => {
    if (!newName) {
      setEditingIndex(null);
    } else {
      handleSave(index, id, newName);
    }
  };

  const handleNameFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
  };

  const handleDetailClose = () => {
    setIsFileDetailSelected(false);
  };

  const deleteFile = async (id: string) => {
    try {
      await deleteFileEmployee(id);
      showSnackbar("Das Dokument wurde erfolgreich gelöscht.", "success");
      fetchSections();
    } catch (error: any) {
      console.error("Error deleting file:", error.message);
      showSnackbar(
        "Beim Löschen des Dokuments ist ein Fehler aufgetreten.",
        "error"
      );
    }
  };

  const handleDelete = (id: string) => {
    deleteFile(id);
  };

  useEffect(() => {
    fetchSections();
  }, [fetchSections, isDownloading]);

  return (
    <Box mt={5} sx={{ width: "100%" }}>
      <Paper>
        <Box p={2}>
          {role !== "clinic-weight" && role !== "clinic-dashboard" ? (
            <>
              <Button
                size="medium"
                variant="contained"
                component="label"
                sx={{ marginX: "15px" }}
                onClick={() => setNewUploadModalOpen(true)}
              >
                Datei Hochladen
              </Button>

              <UploadModalEmployee
                open={newUploadModalOpen}
                onClose={() => setNewUploadModalOpen(false)}
                handleFileChange={handleFileChange}
                isDownloading={isDownloading}
                setFileName={setFileName}
                fileName={fileName}
              />
            </>
          ) : (
            <Button size="medium" variant="contained" component="label">
              Datei Hochladen
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
          )}
        </Box>
        <TableContainer>
          {loading ? (
            <Box
              mt="250px"
              display="flex"
              alignContent="center"
              justifyContent="center"
              height="500px"
            >
              <CircularProgress size={100} />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: "16px", fontWeight: "bold" }}>
                    Dateiname
                  </TableCell>
                  <TableCell sx={{ fontSize: "16px", fontWeight: "bold" }}>
                    hochgeladen von
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Erstellungsdatum
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Aktion
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fileEmployee.length > 0 ? (
                  fileEmployee.map((file, index) =>
                    file.hidden ? null : (
                      <TableRow key={index}>
                        <TableCell width="35%" sx={{ fontWeight: "bold" }}>
                          {editingIndex === index ? (
                            <TextField
                              value={newName}
                              onChange={handleNameFileChange}
                              onBlur={() =>
                                handleBlur(index, file._id, newName)
                              }
                              autoFocus
                              label={file.name}
                              variant="standard"
                              fullWidth
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSave(index, file._id, newName);
                                }
                              }}
                            />
                          ) : (
                            <Box
                              onClick={() => setEditingIndex(index)}
                              sx={{ cursor: "pointer" }}
                            >
                              {file.name ?? file._id}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell width="20%" sx={{ fontWeight: "bold" }}>
                          {file.uploadedBy?.firstName}{" "}
                          {file.uploadedBy?.lastName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          {formatTimestamp(String(file.createdAt))}
                        </TableCell>

                        <TableCell sx={{ textAlign: "center" }}>
                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconButton
                              onClick={() => handleDownloadClicked(file)}
                              sx={{ marginTop: "10px", padding: "10px" }}
                            >
                              <RemoveRedEyeOutlined
                                sx={{ fontWeight: "bold", color: "black" }}
                                fontSize="medium"
                              />
                            </IconButton>
                            <DeletePostModal
                              onDelete={() => handleDelete(file._id)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="error">
                        Es sind keine Dokumente vorhanden
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* FileModal */}

      <Modal open={isFileDetailSelected} onClose={handleDetailClose}>
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: 5,
            width: "900px",
            height: "600px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <IconButton
              onClick={() => handleDownloadTemporaryLink(temporaryUrl)}
              sx={{ marginTop: "10px", padding: "10px" }}
            >
              <CloudDownloadIcon
                sx={{ fontWeight: "bold", color: "black" }}
                fontSize="medium"
              />
            </IconButton>
          </Box>
          {isAPdfFile ? (
            <iframe
              title="file"
              src={temporaryUrl}
              width="100%"
              height="500px"
            />
          ) : isDocxFile ? (
            <Box
              style={{
                width: "100%",
                height: "500px",
                overflowY: "auto",
              }}
              dangerouslySetInnerHTML={{
                __html: docxContent || "Loading...",
              }}
            />
          ) : (
            <ImageList
              sx={{ width: "100%", height: 500 }}
              cols={1}
              rowHeight={450}
            >
              <ImageListItem>
                <img
                  srcSet={temporaryUrl}
                  src={temporaryUrl}
                  alt={"File"}
                  loading="lazy"
                />
              </ImageListItem>
            </ImageList>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={severity} variant="filled" onClose={closeSnackbar}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
